import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { IoArrowBackCircle } from "react-icons/io5";
import "./DriverChecklist.css";

const DriverChecklistView = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    driverName: "",
    badgeNumber: "",
    fleetNumber: "",
    kmReading: "",
    location: "",
    date: "",
    checklist: Array(27).fill(false),
    defects: "",
    actionTaken: "",
    driverSignature: "",
    jmSignature: "",
    technicianSignature: "",
  });

  const [language, setLanguage] = useState("en"); 
  const [srNo, setSrNo] = useState("");
  const [drivers, setDrivers] = useState([]);
  const navigate = useNavigate();

  const normalizeVal = (v) => {
    if (v === undefined || v === null) return null;
    if (typeof v === "string") {
      const t = v.trim();
      if (t === "" || t.toLowerCase() === "null") return null;
      return t;
    }
    return v;
  };

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!id) {
          console.error("Checklist ID is missing");
          return;
        }
        const apiUrl = `${process.env.REACT_APP_BASE_API_URL}/checklist`;
        const response = await axios.post(
          apiUrl,
          { id: decodeURIComponent(id) },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // support: response.data can be { success, data: {...} }
        let data = response.data;
        if (data && data.data) data = data.data;
        data = data || {};

         // normalize keys to lowercase and values
         const norm = {};
         Object.keys(data).forEach((k) => {
           norm[k.toLowerCase()] = normalizeVal(data[k]);
         });

         const checklist = Array.from({ length: 27 }, (_, index) => {
           const key = `checklistitem${index + 1}`;
           return norm[key] === "Y";
         });

         setFormData({
           driverName: norm.drivername || "",
           badgeNumber: norm.badgenumber || "",
           fleetNumber: norm.fleetnumber || "",
           kmReading: norm.kmreading || "",
           location: norm.location || "",
           date: norm.checklistdate || norm.date || "",
           checklist,
           defects: norm.defects || "",
           actionTaken: norm.actiontaken || "",
           driverSignature: norm.driversignature || "",
           jmSignature: norm.jmsignature || "",
           technicianSignature: norm.techniciansignature || "",
         });
         setSrNo(norm.sr_no || "");
       } catch (error) {
         console.error("Error fetching checklist data:", error);
       }
     };
     fetchChecklist();
   }, [id]);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const token = localStorage.getItem("token");
        const apiUrl = `${process.env.REACT_APP_BASE_API_URL}/drivers`;
        const response = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDrivers(response.data);
      } catch (error) {
        console.error("Error fetching drivers data:", error);
      }
    };
    fetchDrivers();
  }, []);

  // const handleDriverChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prevData) => ({ ...prevData, [name]: value }));
  // };

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prevData) => ({ ...prevData, [name]: value }));
  // };

  const checklistItems = [
    { en: "Brakes", ar: "الفرامل" },
    { en: "First Aid Box", ar: "صندوق الإسعافات الأولية" },
    { en: "Fire Extinguisher", ar: "طفاية الحريق" },
    { en: "Jack, Tools & Triangle", ar: "مثلث التحذير والأدوات" },
    { en: "Vehicle Registration Papers", ar: "الملكية" },
    { en: "RAS / Safety Stickers", ar: "ملصقات السلامة" },
    { en: "Trailer Head Board/Bed/Jack", ar: "سرير الشاحنة" },
    { en: "Vehicle Clean", ar: "نظافة المركبة" },
    { en: "Wheel Chocks", ar: "حواجز العجلات" },
    { en: "Body Damage", ar: "أضرار الهيكل" },
    { en: "Tyres", ar: "الإطارات" },
    { en: "Load Restraint", ar: "تثبيت الحمولة" },
    { en: "Battery", ar: "البطارية" },
    { en: "Oil (Level)", ar: "مستوى الزيت" },
    { en: "Water (Level)", ar: "مستوى الماء" },
    { en: "Steering", ar: "المقود" },
    { en: "Wipers/Windscreen", ar: "المساحات والزجاج الأمامي" },
    { en: "Lights/Indicators", ar: "الأضواء والإشارات" },
    { en: "Mirrors", ar: "المرايا" },
    { en: "Speedometer & Speed Restrictor", ar: "عداد السرعة ومحدد السرعة" },
    { en: "Horn (Front & Rev)", ar: "البوق" },
    { en: "Fuel", ar: "الوقود" },
    { en: "Seat Belt", ar: "حزام الأمان" },
    { en: "Radio/Tape", ar: "الراديو" },
    { en: "A/C in Vehicle", ar: "المكيف" },
    { en: "Fuel Anti Theft Lock", ar: "قفل الوقود" },
    {
      en: "5th Wheel/Kingpin condition & Lock",
      ar: "العجلة الخامسة والحالة والقفل",
    },
  ];

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = srNo ? `Checklist_${srNo}` : "Checklist_Print";
    window.print();
    document.title = originalTitle;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));
  };

  const imageBaseUrl = "/";

  return (
    <div className="driver-checklist-page">
      {/* Checklist No Section */}
      {srNo && (
        <div className="checklist-no-container">
          <div className="checklist-no-label">
            {language === "en" ? "Checklist No:" : "رقم قائمة التحقق:"}
          </div>
          <div className="checklist-no-value">{srNo}</div>
          <div className="checklist-no-divider"></div>
        </div>
      )}
      <div className="header-buttons">
        <button className="language-toggle-button" onClick={toggleLanguage}>
          {language === "en" ? "Switch to Arabic" : "Switch to English"}
        </button>
        <button
          className="react-icons-back-button"
          onClick={() => navigate("/driver-checklists/")}
        >
          <IoArrowBackCircle style={{ fontSize: "18px" }} />
        </button>
        <button
          className="cross-back-button"
          onClick={() => navigate("/driver-checklists/")}
        >
          ✕
        </button>
      </div>
      <div className="driver-checklist-header">
        <img
          src={`${imageBaseUrl}headerLogo.jpg`}
          alt="Header Logo"
          className="driver-checklist-logo"
        />
        <div className="driver-checklist-info-box">
          {/* Row 1 */}
          <div className="info-row">
            <div className="input-group">
              <label>
                {language === "en" ? "Driver's Name:" : "اسم السائق:"}
                <span className="required">*</span>
              </label>
              <input
                list="drivers-list"
                type="text"
                name="driverName"
                value={formData.driverName}
                readOnly
                dir={language === "en" ? "ltr" : "rtl"}
              />
            </div>

            <div className="input-group">
              <label>
                {language === "en" ? "Badge #:" : "رقم الشارة:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="badgeNumber"
                value={formData.badgeNumber}
                readOnly
              />
            </div>
          </div>
          {/* Row 2 */}
          <div className="info-row">
            <div className="input-group">
              <label>
                {language === "en" ? "Fleet No:" : "رقم الأسطول:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="fleetNumber"
                value={formData.fleetNumber}
                readOnly
              />
            </div>
            <div className="input-group">
              <label>
                {language === "en" ? "KM's Reading:" : "قراءة الكيلومترات:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="kmReading"
                value={formData.kmReading}
                readOnly
              />
            </div>
          </div>
          {/* Row 3 */}
          <div className="info-row">
            <div className="input-group">
              <label>
                {language === "en" ? "Location:" : "الموقع:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                readOnly
              />
            </div>
            <div className="input-group">
              <label>
                {language === "en" ? "Date:" : "التاريخ:"}
                <span className="required">*</span>
              </label>
              <input type="date" name="date" value={formData.date} readOnly />
            </div>
          </div>

          <datalist id="drivers-list">
            {drivers.map((driver) => (
              <option key={driver.driverName} value={driver.driverName}>
                {driver.driverName}
              </option>
            ))}
          </datalist>
        </div>
      </div>

      <div className="driver-checklist-grid">
        {formData.checklist.map((item, index) => (
          <div key={index} className="driver-checklist-item">
            <span className="checklist-number">{index + 1}</span>
            <img
              src={`${imageBaseUrl}${index + 1}.jpg`}
              alt={`Checklist ${index + 1}`}
              className="driver-checklist-image"
              style={{ width: "46px", height: "46px" }}
            />
            <div
              className="driver-checklist-label"
              style={{ fontSize: "12px" }}
            >
              <span>{checklistItems[index]?.[language]}</span>
            </div>
            <div
              className={`checklist-toggle ${item ? "checked" : "unchecked"}`}
              style={{
                width: "16px",
                height: "15px",
                fontSize: "12px",
                textAlign: "center",
                lineHeight: "20px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: item ? "#d4edda" : "#f8d7da",
                color: item ? "#155724" : "#721c24",
              }}
            >
              {item ? "✔" : "X"}
            </div>
          </div>
        ))}
      </div>

      <div className="driver-checklist-footer">
        <div className="driver-checklist-defects">
          <label>
            {language === "en" ? "Details of Defects:" : "تفاصيل العيوب:"}
          </label>
          <textarea
            name="defects"
            value={formData.defects}
            readOnly
            dir={language === "en" ? "ltr" : "rtl"}
          />
        </div>
        <div className="driver-checklist-action">
          <label>
            {language === "en"
              ? "Action Taken on Reported Defects:"
              : "الإجراءات المتخذة على العيوب المبلغ عنها:"}
          </label>
          <textarea
            name="actionTaken"
            value={formData.actionTaken}
            readOnly
            dir={language === "en" ? "ltr" : "rtl"}
          />
        </div>
      </div>

      <div className="driver-checklist-signatures">
        <div className="driver-checklist-signature">
          <label>
            {language === "en" ? "Driver's Signature:" : "توقيع السائق:"}
          </label>
          {formData.driverSignature ? (
            <img
              src={formData.driverSignature}
              alt="Driver Signature"
              className="driver-signature-preview"
            />
          ) : (
            <span>
              {language === "en" ? "No signature available" : "لا يوجد توقيع"}
            </span>
          )}
        </div>
        <div className="driver-checklist-signature">
          <label>
            {language === "en" ? "JM's Signature:" : "توقيع المدير:"}
          </label>
          {formData.jmSignature ? (
            <img
              src={formData.jmSignature}
              alt="JM Signature"
              className="signature-preview"
            />
          ) : (
            <span>
              {language === "en" ? "No signature available" : "لا يوجد توقيع"}
            </span>
          )}
        </div>
        <div className="driver-checklist-signature">
          <label>
            {language === "en" ? "Technician's Signature:" : "توقيع الفني:"}
          </label>
          {formData.technicianSignature ? (
            <img
              src={formData.technicianSignature}
              alt="Technician Signature"
              className="signature-preview"
            />
          ) : (
            <span>
              {language === "en" ? "No signature available" : "لا يوجد توقيع"}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className="driver-checklist-print"
        onClick={handlePrint}
      >
        {language === "en" ? "Print" : "طباعة"}
      </button>
    </div>
  );
};

export default DriverChecklistView;
